import { useCallback, useMemo, useRef, useState } from 'react';
import { tileKey } from '../../engine/battlefield/movement';
import { readingsAt, threatsTo } from '../../engine/cover/coverCue';
import { screensSeen, screenTipAt } from '../../engine/display/coverScreens';
import useHoldsStill from '../../hooks/useHoldsStill';
import useCoverReach from './useCoverReach';

const NONE = {};

/**
 * Is the cue shown at all on this screen? The display renderer, the viewer's own row (default on), Play's
 * gestures (not building or deploying), positions counting and terrain adjudicating (the ruling, 2026-10-24).
 */
export function cueShown({ renderer, coverCue, building, deploying, positionsCount, ignoreTerrain }) {
  const playing = !building && !deploying;
  const mapRules = positionsCount !== false && !ignoreTerrain;
  return renderer === 'display' && coverCue !== false && playing && mapRules;
}

/**
 * Who wears screens: the actor, and the figure hovered when it is one of this seat's own (a player) or anyone
 * at all (the GM) - the user's ruling after board 10 (2026-10-25).
 */
export function wearersOf({ actorId, hoveredId, isGm, ownIds }) {
  const out = actorId ? [actorId] : [];
  if (hoveredId && hoveredId !== actorId && (isGm || ownIds?.has(hoveredId))) out.push(hoveredId);
  return out;
}

/** Each wearer's readings at where he stands, against the guns that threaten him. */
function useWearerReadings(on, wearers, panel, solids) {
  const { roster, tokens, relationRules, isGm } = panel;
  return useMemo(() => {
    if (!on || !roster || !tokens) return NONE;
    const options = { rules: relationRules, includeHidden: isGm };
    return Object.fromEntries(
      wearers.map((id) => [id, readingsOf(id, roster, tokens, solids, options)]).filter(([, list]) => list?.length),
    );
  }, [on, wearers, roster, tokens, solids, relationRules, isGm]);
}

function readingsOf(id, roster, tokens, solids, options) {
  const c = roster.find((r) => r.id === id);
  const token = tokens[id];
  if (!c || !token) return null;
  return readingsAt(token, token, threatsTo(c, roster, tokens), solids, options);
}

/** The readings as screens on the screen: projected with this frame's camera, split about each figure. */
function projectedFor(readings, tokens, camera) {
  return Object.fromEntries(Object.entries(readings).map(([id, list]) => [id, screensSeen(tokens[id], list, camera)]));
}

/** What makes a figure's screens rise again: where he stands and what each gun reads. */
const riseKeyOf = (token, list) => `${token.x},${token.z}|${list.map((r) => `${r.threatId}:${r.level}`).join(',')}`;

/**
 * useCoverCue - the take-cover cue's state for one render of the map (2026-10-25): which figures wear screens,
 * their readings, what makes them rise, the reach graded while the actor moves, the ghost's readings, the readout
 * and where the pointer is. All of it in the world: the scene puts it on the screen for its own camera
 * (`coverOnScreen`), so a camera frame re-renders nothing here (perf overhaul 2b, 8f). Nothing here is stored or
 * synced: every seat works out its own from what it holds - a player never holds a hidden figure, and GM-only
 * terrain counts only on the GM's screen.
 */
export default function useCoverCue(panel, geo, tools, movement, drag) {
  const on = cueShown({ ...panel, ...tools, coverCue: tools.prefs?.coverCue });
  const wearersKey = wearersOf({ ...panel, ownIds: panel.coverOwnIds }).join('|');
  const wearers = useMemo(() => (wearersKey ? wearersKey.split('|') : []), [wearersKey]);
  const readings = useWearerReadings(on, wearers, panel, geo.solids);
  const tokens = panel.tokens ?? NONE;
  const riseKeys = useMemo(() => {
    const out = {};
    for (const [id, list] of Object.entries(readings)) out[id] = riseKeyOf(tokens[id], list);
    return out;
  }, [readings, tokens]);
  const still = useHoldsStill();
  const [pointer, setPointer] = useState(null);
  const moving = useCoverReach({ on, panel, geo, movement, drag, ground: pointer?.ground ?? null });
  // A pointer move that changes nothing the cue shows keeps the old pointer, so the panel does not re-render at
  // the pointer's rate for a tooltip that is usually nothing (the review) - asked under the camera on the screen
  // at that moment.
  const seen = useRef(null);
  const [shownNow] = useState(lastShown);
  const tileSize = geo.boundary?.tileSize ?? 1;
  seen.current = { tileSize, shown: () => shownNow(readings, tokens, moving.ghost, geo.getCamera()) };
  const setCoverPointer = useCallback(
    (next) => setPointer((prev) => (sameCueReading(prev, next, seen.current) ? prev : next)),
    [],
  );
  const ghostRiseKey = moving.ghost ? riseKeyOf(moving.ghost.token, moving.ghost.readings) : '';
  return {
    on,
    readings,
    tokens,
    pointer,
    riseKeys,
    still,
    ...moving,
    ghostRiseKey,
    setCoverPointer: on ? setCoverPointer : null,
  };
}

/**
 * Whether a pointer move changes nothing the cue shows: the same reach tile under it, and the same tooltip under
 * the camera on the screen now (`cue.shown()`: the screens as `screensShown` gives them, cached per camera).
 */
export function sameCueReading(prev, next, cue) {
  if (!prev || !next) return prev === next;
  const tile = (p) => (p.ground ? tileKey(p.ground.x, p.ground.z, cue.tileSize) : null);
  if (tile(prev) !== tile(next)) return false;
  const { screens, ghostScreens } = cue.shown();
  const text = (p) => tipAt(p, screens, ghostScreens)?.text ?? null;
  return text(prev) === text(next);
}

/** Every wearer's screens and the ghost's, put on the screen under `camera`. Empty readings project nothing. */
export function screensShown(readings, tokens, ghost, camera) {
  return {
    screens: projectedFor(readings, tokens, camera),
    ghostScreens: ghost ? screensSeen(ghost.token, ghost.readings, camera) : null,
  };
}

/**
 * `screensShown`, remembering its last answer: the same readings, tokens, ghost and camera (by identity) are not
 * projected again - a pointer moving inside one camera frame asks the same question many times.
 */
export function lastShown() {
  let last = null;
  return (readings, tokens, ghost, camera) => {
    const hit = last && last.readings === readings && last.tokens === tokens && last.ghost === ghost;
    if (!hit || last.camera !== camera) {
      last = { readings, tokens, ghost, camera, shown: screensShown(readings, tokens, ghost, camera) };
    }
    return last.shown;
  };
}

/** The cue as the scene draws it, with `shown` (`screensShown` for its camera): the screens, the ghost's, the tip. */
export function coverOnScreen(cover, shown) {
  if (!cover) return cover;
  const { screens, ghostScreens } = shown;
  const ghost = cover.ghost && { ...cover.ghost, screens: ghostScreens };
  return { ...cover, screens, ghost, tip: tipAt(cover.pointer, screens, ghostScreens) };
}

/** The tip under the pointer across every figure's screens and the ghost's: the one drawn in front wins. */
function tipAt(pointer, screens, ghostScreens) {
  if (!pointer) return null;
  const text = screenTipAt(pointer, [...Object.values(screens).flat(), ...(ghostScreens ?? [])]);
  return text ? { text, x: pointer.x, y: pointer.y } : null;
}
